/**
 * Vendor shipping-label text -> the fields the shipment matcher already scores.
 *
 * Kolbe and Windsor print opaque carton serials, so src/util/vendorBarcode.js
 * deliberately declines to parse them: the barcode identifies a box, not its
 * contents. What those labels DO carry, in plain text, is the dealer PO -- and
 * on every one of the nine reference photos, in all three vendors' layouts, it
 * is the same default form functions/vendorShippingParse.js parseVendorPO
 * already resolves for Marvin:
 *
 *   Kolbe    "Cust PO. 01800016A"      "PO to Kolbe:  00300062B"
 *   Quaker   "PO:      03900086A"
 *   Windsor  "PO Nbr:  04100058A"
 *
 * That reframes the problem. The PO resolves the SALE exactly, the same way a
 * Marvin tracking number does, so token-overlap scoring is no longer what
 * identifies the order -- it only has to separate the items WITHIN one sale.
 * A weighted guess across every open sale and a weighted guess across the six
 * items on one sale are very different risks.
 *
 * So this module extracts fields; it does not decide anything. Every field is
 * null when absent or ambiguous, on the same principle vendorBarcode.js states:
 * returning a confident-looking result for input that cannot support one is
 * worse than returning nothing.
 *
 * TWO CAUTIONS, both deliberate:
 *
 * 1. The printed sizes are NOT rough openings. Kolbe prints "Overall Box Size"
 *    (the crate), Windsor prints "(FD: 30 x 71)" (the frame), and a rough
 *    opening is the hole in the wall -- they differ by an inch or so and are
 *    not interchangeable. They are returned under their own names and are NOT
 *    folded into a roughOpening field, because feeding a box size into the
 *    matcher's roughOpening signal (weight 3, one of the two strongest) would
 *    manufacture confident wrong matches out of near-misses.
 *
 * 2. OCR digit confusion is not repaired here. poCandidates() offers the
 *    repairs as ALTERNATIVES for the caller to resolve against real sales,
 *    accepting only a unique hit. Repairing in the parser would turn a clean
 *    no-match into a silent wrong match.
 */

/** Vendors whose labels this module knows how to read. */
export const VENDORS = ["Kolbe", "Quaker", "Windsor"];

/**
 * The dealer PO's default form: eight digits (a three-digit team id and a
 * five-digit job, per functions/projects.js) then a single sale letter.
 *
 * functions/vendorShippingParse.js parseVendorPO is the canonical parser and
 * also handles the appended-PO and field-service-order variants Marvin echoes
 * back. Neither variant appears on a printed carton label -- the vendor prints
 * our PO as we gave it -- so only the bare default is matched here. It cannot
 * be imported: only functions/ is uploaded on a functions deploy and only src/
 * is bundled for the app, the same split that forced the second copy of
 * src/util/similarity.js.
 */
const PO_BARE = /^(\d{8})([A-Z])$/;
/** The same shape, found loose in a block of OCR text. */
const PO_LOOSE = /\b(\d{8})([A-Z])\b/g;

/**
 * Characters OCR routinely swaps in the PO, as read -> what it may have been.
 *
 * Only substitutions that cross the digit/letter boundary are listed. A digit
 * misread as another digit ("3" as "8") is undetectable here and is left for
 * the sale lookup to reject.
 */
const OCR_DIGIT_REPAIRS = {
  O: "0",
  Q: "0",
  D: "0",
  I: "1",
  L: "1",
  "|": "1",
  Z: "2",
  S: "5",
  B: "8",
  G: "6",
};

/**
 * Anchors that identify a vendor. Matched against the whole normalized text.
 *
 * Scored rather than first-match-wins: a photo taken on the dock catches more
 * than the label. Photo 1 includes the frame extrusion stencilled
 * "GLASTRA-KOLBE-RH", and photo 9 has a second Quaker label from the next unit
 * in frame, so a single stray token has to be outvoted rather than obeyed.
 */
const VENDOR_ANCHORS = {
  Quaker: [
    /\bSHP\s*[:.]/i,
    /\bQ1\s*#/i,
    /\bUNIT\s*[:.]\s*\d+\s+of\s+\d+/i,
    /\bSD[A-Z]?\d+_L\d+_C\d+/i,
  ],
  Windsor: [
    /\bPO\s*Nbr\b/i,
    /\bSales\s+Order\s*&\s*Line\b/i,
    /\bDelivering\s+plant\b/i,
    /\bRoom\s*ID\b/i,
  ],
  Kolbe: [
    /\bCust\s*PO\b/i,
    /\bPO\s+to\s+Kolbe\b/i,
    /\bLOAD\s*[:.]/i,
    /\bDOCK\s*[:.]/i,
    /\bZONE\s*[:.]/i,
  ],
};

/**
 * Where each vendor prints the PO. Tried in order, on the same line first.
 *
 * Windsor's sits in a three-column box -- "Job Name: | PO Nbr: | Room ID:" as a
 * header row with the values on the row beneath -- so whether the value follows
 * its own label at all depends on how the OCR engine serializes the table. That
 * is why the loose scan below is a first-class path rather than a last resort.
 */
const PO_ANCHORS = {
  Kolbe: [/\bPO\s+to\s+Kolbe\s*[:.]?/i, /\bCust\s*PO\s*[:.]?/i],
  Quaker: [/\bPO\s*[:.]/i],
  Windsor: [/\bPO\s*Nbr\s*[:.]?/i],
};

/** How many lines past an anchor a value may appear before it stops counting. */
const ANCHOR_LOOKAHEAD = 2;

/**
 * Normalize OCR text without destroying its line structure.
 *
 * Line breaks are load-bearing -- they are what separates a boxed table's
 * header row from its values -- so runs of whitespace collapse within a line
 * but the lines themselves survive. Dash and quote variants are unified
 * because vendors and OCR engines disagree about them freely: Windsor's own
 * "REAR GARAGE - UPPER" comes back with an en dash, an em dash or a hyphen
 * depending on the engine and the exposure.
 */
export function normalize(raw) {
  return String(raw || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[‐-―−]/g, "-")
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”″〃]/g, '"')
    .replace(/ /g, " ")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n");
}

/** Non-empty lines, in order. */
function lines(text) {
  return text.split("\n").filter(Boolean);
}

/**
 * Identify the vendor by counting anchor hits.
 *
 * Returns null on a tie or a blank: two vendors' anchors appearing equally
 * often means the photo caught two labels, and picking one would attribute the
 * carton to whichever vendor happened to sort first.
 */
export function detectVendor(text) {
  const scores = VENDORS.map((vendor) => ({
    vendor,
    score: VENDOR_ANCHORS[vendor].filter((re) => re.test(text)).length,
  })).sort((a, b) => b.score - a.score);
  if (!scores[0].score) return null;
  if (scores[1] && scores[1].score === scores[0].score) return null;
  return scores[0].vendor;
}

/**
 * Find the value that follows an anchor, on its line or just below it.
 *
 * @param {Array<string>} rows - normalized non-empty lines
 * @param {RegExp} anchor
 * @param {RegExp} value - must match at the START of what follows
 * @returns {?{value: string, sameLine: boolean}}
 */
function afterAnchor(rows, anchor, value) {
  for (let i = 0; i < rows.length; i++) {
    const hit = anchor.exec(rows[i]);
    if (!hit) continue;
    const tail = rows[i].slice(hit.index + hit[0].length).trim();
    const inline = value.exec(tail);
    if (inline) return { value: inline[0], sameLine: true };
    // The value may have wrapped, or sat under its header in a boxed table.
    for (let j = i + 1; j <= i + ANCHOR_LOOKAHEAD && j < rows.length; j++) {
      const below = value.exec(rows[j].trim());
      if (below && below.index === 0) {
        return { value: below[0], sameLine: false };
      }
    }
  }
  return null;
}

/** Everything after an anchor on its own line, as free text. */
function textAfterAnchor(rows, anchor) {
  for (const row of rows) {
    const hit = anchor.exec(row);
    if (!hit) continue;
    const tail = row.slice(hit.index + hit[0].length).trim();
    if (tail) return tail;
  }
  return null;
}

/** A line that is nothing but a caption -- "PO Nbr:" with no value on it. */
const CAPTION_ONLY = /^[^:]{1,24}:$/;

/**
 * Read a value out of a caption-row-then-value-row table.
 *
 * Windsor boxes three fields together:
 *
 *   Job Name:          PO Nbr:      Room ID:
 *   HEADINGS_GALLOWAY  04100058A    REAR GARAGE - UPPER
 *
 * An OCR engine that serializes this row by row emits the three captions
 * together and then the three values together, so the value never follows its
 * own caption and no amount of lookahead finds it. What survives is the
 * POSITION: the second caption belongs to the second value. Pairing on that is
 * what turns a lucky loose scan into a read that can be trusted.
 *
 * Returns null when the runs do not line up, which is the honest answer -- a
 * table with a caption OCR dropped would otherwise shift every value by one.
 */
function tableValue(rows, anchor) {
  for (let i = 0; i < rows.length; i++) {
    if (!CAPTION_ONLY.test(rows[i]) || !anchor.test(rows[i])) continue;

    let start = i;
    while (start > 0 && CAPTION_ONLY.test(rows[start - 1])) start--;
    let end = i;
    while (end + 1 < rows.length && CAPTION_ONLY.test(rows[end + 1])) end++;

    const position = i - start;
    const values = [];
    for (let j = end + 1; j < rows.length && values.length <= position; j++) {
      if (CAPTION_ONLY.test(rows[j])) break;
      values.push(rows[j]);
    }
    if (values.length > position) return values[position];
  }
  return null;
}

/**
 * Extract the dealer PO, preferring an anchored read over a loose one.
 *
 * `source` travels with the result so the caller can weight the two
 * differently: "anchored" means the digits were found next to the words "PO
 * Nbr", "scan" means they were the only thing on the label shaped like a PO.
 * The second is usually right and occasionally is a date or a part number that
 * happens to fit, so it is reported, not hidden.
 *
 * More than one distinct candidate returns null. Two labels in frame is the
 * common cause, and picking either one attributes the carton to a real sale
 * that it may not belong to -- the worst failure available here.
 *
 * @param {string} text - normalized
 * @param {?string} vendor
 * @returns {?{raw, projectID, saleID, source: "anchored"|"scan"}}
 */
export function extractPO(text, vendor) {
  const rows = lines(text);

  // Uniqueness is settled first, across the whole text, because a PO found
  // beside its caption is no safer than a loose one when a SECOND label is in
  // frame -- the anchored read simply reports whichever carton sorted first.
  // Photo 9 catches the next unit's label in shot, so this is the ordinary
  // case, not the exotic one. Attributing a carton to a real but wrong sale is
  // the worst failure available here and the one a receiver is least likely to
  // catch, so two candidates yield nothing at all.
  const distinct = new Map();
  for (const match of text.matchAll(new RegExp(PO_LOOSE.source, "g"))) {
    distinct.set(match[0], { projectID: match[1], saleID: match[2] });
  }
  if (distinct.size !== 1) return null;
  const [raw, parts] = [...distinct.entries()][0];

  // The value is the same either way; the anchor only says how much to trust
  // it. Both readings are tried on their own merits, because taking whichever
  // came first would let a near-miss shadow the real hit: on Windsor's boxed
  // label the lookahead reaches "HEADINGS_GALLOWAY", the job name one row
  // above the PO, and the anchored path would report nothing.
  for (const anchor of PO_ANCHORS[vendor] || []) {
    const hit = afterAnchor(rows, anchor, /[0-9A-Z|]{8,9}[A-Z0-9|]?/);
    for (const candidate of [hit && hit.value, tableValue(rows, anchor)]) {
      if (candidate === raw) return { raw, ...parts, source: "anchored" };
    }
  }
  return { raw, ...parts, source: "scan" };
}

/**
 * Every reading of a PO-shaped string that OCR could have meant, best first.
 *
 * The caller resolves these against real sales and accepts a candidate only
 * when exactly one exists -- which is what makes the repair safe. Doing it here
 * instead, with no sale list to check against, would convert a clean no-match
 * into a wrong match.
 *
 * "O18OOO16A" -> ["01800016A"]        (all five swaps are unambiguous)
 * "01800016A" -> ["01800016A"]        (nothing to repair)
 *
 * @param {string} raw - a token that ALMOST matches the PO shape
 * @returns {Array<string>} zero or more candidate POs, literal read first
 */
export function poCandidates(raw) {
  const token = String(raw || "")
    .toUpperCase()
    .replace(/\s+/g, "");
  if (!token) return [];

  const out = [];
  if (PO_BARE.test(token)) out.push(token);
  if (token.length !== 9) return out;

  // Only the first eight characters may be repaired into digits; the ninth is
  // the sale letter and must stay a letter.
  const head = token.slice(0, 8);
  const tail = token.slice(8);
  if (!/^[A-Z]$/.test(tail)) return out;

  const repaired = [...head]
    .map((ch) => (/\d/.test(ch) ? ch : OCR_DIGIT_REPAIRS[ch] || null))
    .join("");
  if (repaired.length === 8 && /^\d{8}$/.test(repaired)) {
    const candidate = repaired + tail;
    if (!out.includes(candidate)) out.push(candidate);
  }
  return out;
}

/** "29 1/2" -> 29.5, "96" -> 96, "37 49/64" -> 37.765625. */
export function inchesToDecimal(value) {
  const cleaned = String(value || "")
    .replace(/["″]/g, "")
    .trim();
  const m = cleaned.match(/^(\d+(?:\.\d+)?)(?:\s+(\d+)\/(\d+))?$/);
  if (!m) return null;
  const whole = Number(m[1]);
  if (!m[2]) return whole;
  const denominator = Number(m[3]);
  if (!denominator) return whole;
  return whole + Number(m[2]) / denominator;
}

const DIMENSION = "(\\d+(?:\\.\\d+)?(?:\\s+\\d+\\/\\d+)?)";
const SIZE_PAIR = new RegExp(
  `${DIMENSION}\\s*["″]?\\s*[xX]\\s*${DIMENSION}\\s*["″]?`
);

/** Parse a "W x H" pair into decimals, or null if either side is unreadable. */
function sizePair(fragment) {
  const m = fragment && SIZE_PAIR.exec(fragment);
  if (!m) return null;
  const width = inchesToDecimal(m[1]);
  const height = inchesToDecimal(m[2]);
  return width === null || height === null ? null : { width, height };
}

/**
 * The printed size, under the name the vendor gives it.
 *
 * Deliberately never called roughOpening -- see the header. `kind` is what the
 * label actually said, so a caller that later learns how to convert a given
 * vendor's box size into a rough opening can do it knowingly.
 *
 * @returns {?{kind: "boxSize"|"frameDimension"|"unitSize", width, height}}
 */
export function extractSize(text, vendor) {
  // Captioned sizes are read off a flattened copy. Spec text wraps wherever the
  // label runs out of width, and it wraps mid-measurement: Kolbe splits
  // "Overall / Box Size" across two lines on one photo and "Panel Outside
  // Measurement 37 / 49/64"" across two on another, so a line-bound search
  // finds neither. Quaker's uncaptioned pair below still needs its line.
  const flat = text.replace(/\n/g, " ").replace(/\s+/g, " ");

  if (vendor === "Kolbe") {
    const m = /Overall\s+Box\s+Size\s*[:.]?\s*([^,;]+)/i.exec(flat);
    const size = sizePair(m && m[1]);
    if (size) return { kind: "boxSize", ...size };
    // A PANEL, not the unit. A multi-slide door ships as one carton per panel
    // and each carton prints its own panel's measurement, while the sale item
    // carries the assembly's -- "Frame Size 180" x 108"" for the five-panel
    // door whose panels are 37 49/64" wide. Naming this unitSize let it reach
    // the printedSize signal, where it did not merely fail to help: the weight
    // became available and earned nothing, dragging the only correct pairing
    // below the floor. Part against whole is the same category error as a
    // crate against a hole in a wall.
    const outside = /Panel\s+Outside\s+Measurement\s*[:.]?\s*([^,;]+)/i.exec(
      flat
    );
    const panel = sizePair(outside && outside[1]);
    return panel ? { kind: "panelSize", ...panel } : null;
  }

  if (vendor === "Windsor") {
    const m = /\(\s*FD\s*[:.]?\s*([^)]+)\)/i.exec(flat);
    const size = sizePair(m && m[1]);
    return size ? { kind: "frameDimension", ...size } : null;
  }

  if (vendor === "Quaker") {
    // Quaker prints the size bare, with no caption at all -- "59 1/2 X 46 1/2"
    // on its own. Requiring the line to hold nothing else is what keeps it from
    // swallowing a dimension out of the middle of a description.
    for (const row of lines(text)) {
      const stripped = row.replace(/^(?:WH|BB|V\d+\s*P\/N)\s+/i, "").trim();
      const size = sizePair(stripped);
      if (size && SIZE_PAIR.exec(stripped).index === 0) {
        return { kind: "unitSize", ...size };
      }
    }
  }

  return null;
}

/**
 * The room the unit is destined for -- the matcher's `room` signal.
 *
 * Windsor prints it twice: once in the boxed table under "Room ID:", and again
 * inside the description as "(Room ID REAR GARAGE - UPPER)". The second is on
 * the same line as its own caption, so it survives whatever the OCR engine does
 * to the table, and is tried first for exactly that reason.
 */
export function extractRoom(text, vendor) {
  const rows = lines(text);

  if (vendor === "Windsor") {
    const inDescription = /\(\s*Room\s*ID\s+([^)]+)\)/i.exec(text);
    if (inDescription) return inDescription[1].trim();
    const anchor = /\bRoom\s*ID\s*[:.]/i;
    return textAfterAnchor(rows, anchor) || tableValue(rows, anchor);
  }
  if (vendor === "Kolbe") return textAfterAnchor(rows, /\bLabel\s*[:.]/i);
  if (vendor === "Quaker") return textAfterAnchor(rows, /\bLOC\s*[:.]/i);
  return null;
}

/**
 * What a carton says about being a PART of something rather than a thing.
 *
 * A Kolbe multi-slide door ships one carton per panel. The sale holds the door
 * as a single item with its panels hanging off it by parentID, so no carton on
 * that pallet is the item -- each is a fraction of one, and the matcher can
 * never be right about which sale item a panel "is".
 *
 * The label says so plainly, and in three places at once:
 *
 *   "Panel 3, 1.1, Panel 3"      which panel this carton holds
 *   "(5-Panel"                    how many the door has
 *   "Panel Outside Measurement"   a dimension belonging to the panel
 *
 * Reported so the receiver can be told what they are holding instead of being
 * told nothing matched. `index` is taken only when every "Panel N" on the label
 * agrees; a label naming two different panels is describing a carton this
 * cannot summarise, and a wrong panel number on screen is worse than none.
 *
 * @returns {?{kind: "panel", index: ?string, of: ?string}}
 */
export function extractComponent(text, vendor) {
  if (vendor !== "Kolbe") return null;
  // "(5-Panel" -- the digit precedes the word, so this cannot collide with the
  // "Panel 3" form below.
  const total = /\b(\d+)-Panel\b/i.exec(text);
  const indices = new Set(
    [...text.matchAll(/\bPanel\s+(\d+)\b/gi)].map((m) => m[1])
  );
  if (!total && !indices.size) return null;
  return {
    kind: "panel",
    index: indices.size === 1 ? [...indices][0] : null,
    of: total ? total[1] : null,
  };
}

/**
 * The callout number a Windsor carton prints.
 *
 * Windsor imports its items in two formats. One names the dimension --
 * "(FD: 30 x 71)" -- and extractSize handles it. The other names a callout
 * instead: "Windsor Pinnacle Clad Casement - Left / Complete Unit - Callout
 * 3244". On that format there is no FD, and on the sales carrying it there were
 * no item locations either, so a scanned carton had nothing at all to match on.
 *
 * The carton prints the same number, as "DCS 3244" inside the mull description:
 *
 *   label  "(A1) DCS 3244 - (A2) DCS 3244 - Standard Mulls"
 *   item   "Complete Unit - Callout 3244"          <- the only one of four
 *
 * It goes to callNumber, the weight-3 signal, and the comparison there is exact
 * equality -- which is what this needs. The rivals on that same sale are 4234,
 * 3636 and 1444; 4234 is a digit permutation of 3244, so anything approximate
 * would be worse than nothing.
 *
 * A mull whose lites carry DIFFERENT callouts returns null. One carton holding
 * two products cannot honestly claim one call number, and "CustomCustom" --
 * which is what the other mulled label in the reference set prints where a
 * number would go -- yields nothing to match, correctly.
 *
 * Rests on a single numeric sample. scripts/probe-label-match.mjs is how to
 * widen it: point --ocr at more labels and see whether the pattern holds.
 */
export function extractCallout(text, vendor) {
  if (vendor !== "Windsor") return null;
  const found = new Set();
  for (const m of text.matchAll(/\bDCS\s+(\d{3,5})\b/gi)) found.add(m[1]);
  return found.size === 1 ? [...found][0] : null;
}

/**
 * Vendor order, line and unit.
 *
 * Line numbers keep their leading zeros. The label, the barcode and the SHP
 * field all agree on "004", and coercing a vendor line number to Number has
 * bitten this codebase before -- Marvin's "10a" accessory lines became 0.
 */
export function extractOrderLine(text, vendor) {
  const rows = lines(text);
  const empty = {
    vendorOrder: null,
    lineNumber: null,
    unit: null,
    unitsOnLine: null,
  };

  if (vendor === "Quaker") {
    const shp = /\bS([A-Z]{0,2}\d+)_L(\d{3})_C(\d{4})\b/i.exec(text);
    const so = afterAnchor(rows, /\bSO\s*[:.]/i, /[A-Z]{0,2}\d{3,}/i);
    const line = afterAnchor(rows, /\bLINE\s*[:.]/i, /\d{2,4}/);
    const unit = /\bUNIT\s*[:.]\s*(\d{1,4})\s+of\s+(\d{1,4})\b/i.exec(text);
    return {
      // The SHP field repeats all three and is the tie-breaker when the
      // stacked SO/LINE/UNIT column comes back out of order.
      vendorOrder:
        (so && so.value.toUpperCase()) || (shp && shp[1].toUpperCase()) || null,
      lineNumber: (line && line.value) || (shp && shp[2]) || null,
      unit: (unit && unit[1]) || (shp && shp[3]) || null,
      unitsOnLine: (unit && unit[2]) || null,
    };
  }

  if (vendor === "Kolbe") {
    // "1354975 020-1" -- order, line, unit -- printed above the carton barcode.
    const m = /\b(\d{6,8})\s+(\d{3})-(\d{1,3})\b/.exec(text);
    if (m) {
      return {
        vendorOrder: m[1],
        lineNumber: m[2],
        unit: m[3],
        unitsOnLine: null,
      };
    }
    const order = afterAnchor(rows, /\bOrder\s*#\s*/i, /\d{6,8}/);
    return order ? { ...empty, vendorOrder: order.value } : empty;
  }

  if (vendor === "Windsor") {
    // "Sales Order & Line:" heads a two-column pair; the values may wrap below.
    const pair =
      /\bSales\s+Order\s*&\s*Line\s*[:.]?\s*(\d{6,8})\s+(\d{1,4})\b/i.exec(
        text
      );
    if (pair) {
      return {
        vendorOrder: pair[1],
        lineNumber: pair[2],
        unit: null,
        unitsOnLine: null,
      };
    }
    const below = afterAnchor(
      rows,
      /\bSales\s+Order\s*&\s*Line\s*[:.]?/i,
      /\d{6,8}\s+\d{1,4}/
    );
    if (below) {
      const [order, line] = below.value.split(/\s+/);
      return {
        vendorOrder: order,
        lineNumber: line,
        unit: null,
        unitsOnLine: null,
      };
    }
  }

  return empty;
}

/** "Qty: 1" -- Windsor is the only one of the three that prints a quantity. */
export function extractQuantity(text) {
  const m = /\bQty\s*[:.]?\s*(\d{1,4})\b/i.exec(text);
  if (!m) return null;
  const value = Number(m[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** The job or quote the order was placed under. Useful to a human, not scored. */
export function extractJobName(text, vendor) {
  const rows = lines(text);
  if (vendor === "Windsor") {
    const anchor = /\bJob\s*Name\s*[:.]/i;
    return textAfterAnchor(rows, anchor) || tableValue(rows, anchor);
  }
  if (vendor === "Quaker") return textAfterAnchor(rows, /\bJOB\s*[:.]/i);
  if (vendor === "Kolbe")
    return textAfterAnchor(rows, /\bQuote\s*Name\s*[:.]/i);
  return null;
}

/**
 * Every caption printed on the three layouts, so prose can be told from
 * structure without guessing at a shape.
 *
 * A generic "starts with a word and a colon" rule cannot do this job: the spec
 * text wraps mid-phrase, and "Overall / Box Size: 35 1/2" X 65 1/2"" puts a
 * line that looks exactly like a caption in the middle of the description.
 * Enumerating what the labels actually say is longer and duller than a pattern,
 * and it is the only version that gets that line right.
 */
const CAPTIONS = new RegExp(
  "^(?:" +
    [
      // Kolbe
      "Printed",
      "LOAD",
      "DOCK",
      "ZONE",
      "ROUTE",
      "UOM",
      "Load Date",
      "Quote Name",
      "Cust PO",
      "PO to Kolbe",
      "Label",
      "Order #",
      // Quaker
      "SO",
      "Q1#",
      "LINE",
      "UNIT",
      "PO",
      "SHP",
      "LOC",
      "JOB",
      "WEIGHT",
      "SHIP ITEM",
      "LIFTING ASSISTANCE",
      // Windsor
      "Ship Date",
      "Sales Order & Line",
      "Qty",
      "Customer",
      "Ship To",
      "Job Name",
      "PO Nbr",
      "Room ID",
      "Delivering plant",
    ]
      .map((caption) => caption.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|") +
    ")\\s*[:.]?(?:\\s|$)",
  "i"
);

/** A line carrying no prose: a serial, a date, a plant number, a stencil. */
function isStructural(row) {
  return (
    CAPTIONS.test(row) ||
    !/[a-z]/.test(row) ||
    /^[\d\s/:.-]+(?:AM|PM)?$/i.test(row)
  );
}

/**
 * The product description -- the matcher's `description` signal.
 *
 * The spec text is the longest unbroken run of prose on the label, on all three
 * layouts. Taking the longest run rather than the first is what skips the
 * dealer name and address block at the top; trimming lines with no lowercase
 * off each end is what stops a shouted "REAR GARAGE - UPPER" being read as the
 * opening words of a Windsor description.
 */
export function extractDescription(text) {
  const rows = lines(text);
  const runs = [];
  let current = [];
  for (const row of rows) {
    if (isStructural(row)) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push(row);
    }
  }
  if (current.length) runs.push(current);

  const best = runs
    .map((run) => {
      const trimmed = [...run];
      while (trimmed.length && !/[a-z]/.test(trimmed[0])) trimmed.shift();
      while (trimmed.length && !/[a-z]/.test(trimmed[trimmed.length - 1])) {
        trimmed.pop();
      }
      return trimmed.join(" ").replace(/\s+/g, " ").trim();
    })
    .filter(isProse)
    .sort((a, b) => b.length - a.length)[0];

  return best || null;
}

/** Below this a run is a stray mark or a fragment, not a description. */
const PROSE_MIN_LENGTH = 20;
const PROSE_MIN_WORDS = 3;

/**
 * Whether a run of lines is a description at all.
 *
 * Quaker's labels carry no product description -- the spec simply is not on
 * them -- and without this the longest run of "prose" on one is the stray "x"
 * marking the SHIP ITEM box, or, when the next unit's label is in shot, the
 * "0007 of 00" bleeding in from its torn edge. Both are the failure this module
 * exists to avoid: a confident-looking value that the input cannot support.
 * A real description runs to a line of text with several words in it.
 */
function isProse(value) {
  if (value.length < PROSE_MIN_LENGTH) return false;
  const words = value.match(/[A-Za-z]{3,}/g) || [];
  return words.length >= PROSE_MIN_WORDS;
}

/**
 * Read a vendor shipping label's OCR text into matcher-ready fields.
 *
 * Nothing here decides what the carton is. It reports what the label says, with
 * enough provenance (`vendor`, `po.source`) for the caller to weigh it.
 *
 * @param {string} raw - OCR text, any engine
 * @returns {{
 *   vendor: ?string,
 *   po: ?{raw, projectID, saleID, source},
 *   vendorOrder: ?string,
 *   lineNumber: ?string,
 *   unit: ?string,
 *   unitsOnLine: ?string,
 *   quantity: ?number,
 *   room: ?string,
 *   size: ?{kind, width, height},
 *   jobName: ?string,
 *   description: ?string,
 * }}
 */
export function extractLabelFields(raw) {
  const text = normalize(raw);
  const vendor = detectVendor(text);
  const { vendorOrder, lineNumber, unit, unitsOnLine } = extractOrderLine(
    text,
    vendor
  );

  return {
    vendor,
    po: extractPO(text, vendor),
    vendorOrder,
    lineNumber,
    unit,
    unitsOnLine,
    quantity: extractQuantity(text),
    room: extractRoom(text, vendor),
    size: extractSize(text, vendor),
    component: extractComponent(text, vendor),
    callout: extractCallout(text, vendor),
    jobName: extractJobName(text, vendor),
    // Held back until a vendor is known, unlike the PO above. The PO has a
    // shape rare enough to vouch for itself wherever it turns up; a
    // description has none -- any prose at all satisfies it -- so on text that
    // was never identified as a label, reporting one invents a signal out of
    // whatever else was in the photo.
    description: vendor ? extractDescription(text) : null,
  };
}
